import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import {
  faLocationDot,
  faMobileScreenButton,
  faPaperPlane,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import {
  formatDisplayPhone,
  condenseOfficeHours,
  type OfficeHourEntry,
} from './PropertyInfoPanel';

// ============================================================================
// TourBlock
// ----------------------------------------------------------------------------
// Forked from ContactBlock for the Schedule A Tour page. The shape is
// identical — hero image, primary-color callout, gray card with a form on
// the left and a logo / contact-info row underneath — but the form region
// is pinned to the multihub-tour-scheduler block instead of dynamically
// swapping in either the lead-capture form or the tour scheduler at the
// section level. There is no props interface; pages drop this in with just
// id + slot.
// ============================================================================

// ============================================================================
// Text color on the gray card
// ----------------------------------------------------------------------------
// The card background is pinned to a static light gray (#f3f4f6) — see
// `.tb-card` CSS below — so we do not need the Footer's WCAG luminance swap
// here. Text on the card is always the dark variant.
// ============================================================================

const TEXT_ON_CARD = '#1a1a1a';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const heroImageDef: SlotGroupDefinition = {
  label: 'Hero Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Schedule a tour background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Use the property standard (primary) logo here — not a mono variant.
// The card background is a static light gray (#f3f4f6) and the primary
// logo is expected to read against any reasonable background, so we no
// longer flip between mono_black / mono_white based on surface luminance.
// The user can still pick a different variant via the slot picker.
//
// NOTE: keep comments out of this object literal. The schema-linter
// static extractor uses a regex brace-balancer that does not strip JS
// comments, and treats unmatched apostrophes as string openers — which
// eats subsequent content and breaks extraction of the surrounding
// const, cascading into a contentSchema that cannot be statically
// evaluated at preview time.
const logoDef: SlotGroupDefinition = {
  label: 'Brand Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: [
        'property_primary_logo',
        'property_secondary_logo',
        'property_mono_black_logo',
        'property_mono_white_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Logo',
    },
  },
};

const emailLinkDef: SlotGroupDefinition = {
  label: 'Email Link',
  fields: {
    url: {
      label: 'Email Address',
      type: 'email',
      defaultField: 'property_email',
    },
    label: {
      label: 'Display Text',
      type: 'text',
      defaultValue: 'Email Us',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ── Social link defs (icons rendered below the logo) ───────────────────────
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function TourBlock() {
  // ── Hero image ──────────────────────────────────────────────────────────
  const heroImage = useSlotGroup('tourblock.hero_image', heroImageDef);

  // ── Form block (left, overlaps hero) ────────────────────────────────────
  // Pinned to the tour scheduler. Editors can still swap the block via the
  // inline editor, but there is no section-level prop that dynamically
  // picks a different form like ContactBlock has.
  const formSlot = useBlockSlot('tourblock.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  // ── Heading / blurb (right, overlaps hero) ──────────────────────────────
  // Headings/preheading/form-heading are richtext so editors can apply
  // inline emphasis (e.g. <em>, <strong>) and embed property fields.

  // ── Logo (lower container) ──────────────────────────────────────────────
  // Uses the property standard (primary) logo. No contrast-driven swap
  // between mono variants — the card sits on a static light gray and the
  // standard logo is expected to be legible there.
  const logo = useSlotGroup('tourblock.logo', logoDef);

  // ── Social links (rendered below the logo) ──────────────────────────────
  const facebook = useSlotGroup('tourblock.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('tourblock.instagram_link', instagramLinkDef);
  const google = useSlotGroup('tourblock.google_link', googleLinkDef);
  const hasAnySocial = Boolean(facebook.url || instagram.url || google.url);

  // ── Address / phone / email / hours (lower container) ───────────────────
  const address = useContentToken('tourblock.address');
  const phone = useContentToken('tourblock.phone') || '';
  const emailLink = useSlotGroup('tourblock.email', emailLinkDef);

  const officeHours = useCollectionBinding<OfficeHourEntry>('tourblock.office_hours', {
    collection: 'office_hours',
  });

  const phoneDisplay = phone ? formatDisplayPhone(phone) : '';
  const hasHours = officeHours.length > 0;
  const hoursRows = hasHours ? condenseOfficeHours(officeHours) : [];

  return (
    <section className="tb-section">
      {/* ── HERO IMAGE (full width, white page bg around) ──────────────── */}
      <EditableSlotGroup groupPath="tourblock.hero_image" definition={heroImageDef}>
        <div className="tb-hero-image-wrap">
          {heroImage.image ? (
            <VibesitesImage
              url={heroImage.image}
              alt={heroImage.alt || ''}
              className="tb-hero-image"
            />
          ) : (
            <div className="tb-hero-image-fallback" aria-hidden="true" />
          )}
        </div>
      </EditableSlotGroup>

      {/* ── GRAY CARD: holds form + callout + lower row ────────────────── */}
      <div className="tb-card-wrap">
        <div className="tb-card">
          <div className="tb-overlap-grid">

            {/* Form */}
            <div className="tb-form-col">
              <RichTextContent
                path="tourblock.form_heading"
                as="h3"
                className="tb-form-heading"
              />
              <EditableToken path="tourblock.form_block" as="div">
                {formSlot ? (
                  <div className="tb-form-wrap">
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  </div>
                ) : (
                  <div className="tb-form-empty">
                    <p>No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>

            {/* Right column: callout (overlaps hero) + logo|info row */}
            <div className="tb-info-col">
              {/* Primary-color heading + blurb square (overlaps hero) */}
              <div className="tb-callout">
                <RichTextContent
                  path="tourblock.preheading"
                  as="p"
                  className="tb-callout-preheading"
                />
                <RichTextContent
                  path="tourblock.heading"
                  as="h2"
                  className="tb-callout-heading"
                />
                <RichTextContent
                  path="tourblock.blurb"
                  defaultValue="<p>Pick a time that works for you and our leasing team will be ready to show you around in person.</p>"
                  as="div"
                  className="tb-callout-blurb"
                />
              </div>

              {/* Below callout: logo (left) | vertical divider | contact info (right).
                  Sits in the right column under the callout, content centered. */}
              <div className="tb-lower-grid">
                <div className="tb-logo-col">
                  <EditableSlotGroup groupPath="tourblock.logo" definition={logoDef}>
                    <div className="tb-logo-wrap">
                      {logo.image ? (
                        <VibesitesImage
                          url={logo.image}
                          alt={logo.alt || 'Property Logo'}
                          className="tb-logo"
                        />
                      ) : (
                        <div className="tb-logo-fallback" aria-hidden="true" />
                      )}
                    </div>
                  </EditableSlotGroup>

                  {hasAnySocial && (
                    <div className="tb-social-row" role="list" aria-label="Social links">
                      {facebook.url && (
                        <EditableSlotGroup groupPath="tourblock.facebook_link" definition={facebookLinkDef}>
                          <a
                            href={facebook.url}
                            target={facebook._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={facebook.label || 'Facebook'}
                            className="tb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faFacebook} className="tb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                      {instagram.url && (
                        <EditableSlotGroup groupPath="tourblock.instagram_link" definition={instagramLinkDef}>
                          <a
                            href={instagram.url}
                            target={instagram._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={instagram.label || 'Instagram'}
                            className="tb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faInstagram} className="tb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                      {google.url && (
                        <EditableSlotGroup groupPath="tourblock.google_link" definition={googleLinkDef}>
                          <a
                            href={google.url}
                            target={google._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={google.label || 'Google'}
                            className="tb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faGoogle} className="tb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                    </div>
                  )}
                </div>

                <div className="tb-info-list" style={{ color: TEXT_ON_CARD }}>
                  {/* Address */}
                  <div className="tb-info-item">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="tb-info-icon"
                      aria-hidden="true"
                    />
                    <EditableToken
                      path="tourblock.address"
                      as="p"
                      className="tb-info-value"
                    >
                      {address || ''}
                    </EditableToken>
                  </div>

                  {/* Phone */}
                  <div className="tb-info-item">
                    <FontAwesomeIcon
                      icon={faMobileScreenButton}
                      className="tb-info-icon"
                      aria-hidden="true"
                    />
                    <p className="tb-info-value">
                      {phone ? (
                        <a
                          href={`tel:${phone}`}
                          className="dniphone dniphonehref tb-info-link"
                        >
                          <EditableToken path="tourblock.phone" as="span">
                            {phoneDisplay}
                          </EditableToken>
                        </a>
                      ) : (
                        <EditableToken path="tourblock.phone" as="span">
                          {phoneDisplay}
                        </EditableToken>
                      )}
                    </p>
                  </div>

                  {/* Email */}
                  <EditableSlotGroup groupPath="tourblock.email" definition={emailLinkDef}>
                    <div className="tb-info-item">
                      <FontAwesomeIcon
                        icon={faPaperPlane}
                        className="tb-info-icon"
                        aria-hidden="true"
                      />
                      <p className="tb-info-value">
                        {emailLink.url ? (
                          <a
                            href={`mailto:${emailLink.url}`}
                            target={emailLink._target || '_self'}
                            className="tb-info-link"
                          >
                            {emailLink.label || emailLink.url}
                          </a>
                        ) : (
                          <span>{emailLink.label || ''}</span>
                        )}
                      </p>
                    </div>
                  </EditableSlotGroup>

                  {/* Office hours */}
                  {hasHours && (
                    <div className="tb-info-item tb-info-item-hours">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="tb-info-icon"
                        aria-hidden="true"
                      />
                      <div className="tb-info-value">
                        <div className="tb-hours-grid">
                          {hoursRows.map((row, i) => (
                            <div key={i} className="tb-hours-row">
                              <span className="tb-hours-day">{row.dayLabel}</span>
                              <span className="tb-hours-time">{row.timeLabel}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tb-section {
          background: var(--vibe-color-background, #ffffff);
          color: var(--vibe-color-text);
          padding-bottom: 1.5rem;
        }
        @media (min-width: 768px) {
          .tb-section { padding-bottom: 2.5rem; }
        }

        /* ============================================================
           HERO IMAGE — full width, sits on the white page background
           (no gray panel under it)
           ============================================================ */
        .tb-hero-image-wrap {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .tb-hero-image-wrap { height: 260px; }
        }
        @media (min-width: 992px) {
          .tb-hero-image-wrap { height: 320px; }
        }

        /* ============================================================
           GRAY CARD — wraps form + callout + lower row.
           Margins around the card show the white page background on
           every edge; internal padding gives breathing room from the
           gray edges to the content.
           ============================================================ */
        .tb-card-wrap {
          padding: 0 1.25rem;
          margin-top: 1.5rem;
        }
        @media (min-width: 768px) {
          .tb-card-wrap {
            padding: 0 2rem;
            margin-top: 2rem;
          }
        }
        @media (min-width: 1200px) {
          .tb-card-wrap {
            padding: 0 3rem;
          }
        }

        /* Gray card background is pinned to the same static light gray
           the Header upper utility row uses (#f3f4f6). NOT a theme
           token — this color should NOT shift with the property
           surface/background tokens, by explicit request to match the
           top of the page. See Header.tsx (.oasis-util-row) for the
           sibling usage. */
        .tb-card {
          background: #f3f4f6;
          max-width: 80rem;
          margin: 0 auto;
          padding: 2.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .tb-card { padding: 3.5rem 2.5rem; }
        }
        @media (min-width: 992px) {
          .tb-card { padding: 4rem 3rem; }
        }

        .tb-hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .tb-hero-image-fallback {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            var(--vibe-color-secondary) 100%
          );
        }

        .tb-overlap-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 992px) {
          /* Form column is narrower than the callout so the form feels less
             dominant; callout takes the wider share on the right. */
          .tb-overlap-grid {
            grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
            gap: 2.5rem;
            align-items: start;
          }
        }

        /* Form column — sits transparent on the gray card. No drop
           shadow, no border-radius, no wrapper background. */
        .tb-form-col { width: 100%; }

        /* Heading above the form. Uses the theme heading font and the
           theme primary color. */
        .tb-form-heading {
          font-family: var(--vibe-font-heading);
          color: var(--vibe-color-primary);
          font-size: 1.25rem;
          line-height: 1.25;
          font-weight: 600;
          letter-spacing: 0.01em;
          margin: 0 0 1rem 0;
        }
        @media (min-width: 768px) {
          .tb-form-heading {
            font-size: 1.5rem;
            margin-bottom: 1.25rem;
          }
        }

        .tb-form-wrap {
          background: transparent;
        }
        /* The selected form block (e.g. multihub-tour-scheduler) renders
           its own "card" div with inline styles: white background, shadow,
           padding, and an <h3> title. Strip those so the form blends
           into the gray tb-card, and hide the form heading. */
        .tb-form-wrap > div {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
        }
        .tb-form-wrap > div > h3 {
          display: none !important;
        }
        /* Hide the empty spacer div the form renders when it has a
           header but no subheader (inline style marginBottom: 1.5rem). */
        .tb-form-wrap > div > div[style*="marginBottom"] {
          display: none !important;
        }
        /* Keep the success-state overlay matching the gray card. */
        .tb-form-wrap .mh-lead-form-success {
          background: #f3f4f6 !important;
        }
        /* Stack first/last name vertically at full form width. The form
           renders these as a 2-col grid via inline styles; collapse it
           to a single column. The existing 1rem grid gap becomes the
           vertical spacing between the stacked fields. */
        .tb-form-wrap form > div:has(> div > input[name="first_name"]) {
          grid-template-columns: 1fr !important;
        }
        /* Body-text treatment for form fields — match the rest of the
           page (Nunito Sans body font, 14px / 0.875rem). The form block sets
           font-size:1rem inline on inputs/select/textarea, so override
           with !important. Placeholders inherit font-size from the
           field. font-family is already var(--vibe-font-body) in the
           vendor block. */
        .tb-form-wrap input,
        .tb-form-wrap select,
        .tb-form-wrap textarea {
          font-family: var(--vibe-font-body) !important;
          font-size: 0.875rem !important;
        }
        /* Opt-in / consent text — already small (0.8125rem) but pin the
           font to body for safety. */
        .tb-form-wrap label,
        .tb-form-wrap label span {
          font-family: var(--vibe-font-body) !important;
        }
        /* Submit button: half the form width, left-aligned, and the
           visible label is swapped to "SUBMIT" (uppercase) via a
           pseudo-element. The real text node is collapsed with
           font-size:0; the ::before paints the new label. The disabled
           state (form is submitting) swaps to "SUBMITTING..." to match
           the form original loading affordance.
           Note: the DOM text node remains the block content value, so
           the button accessible name is whatever the form block
           supplies. To make screen-reader output match exactly, also
           edit the block "Button Text" field in the page editor. */
        .tb-form-wrap form button[type="submit"] {
          width: 50% !important;
          padding: 0.75rem 1.5rem !important;
          font-size: 0 !important;
          letter-spacing: 0 !important;
          transition: background-color 0.2s ease, color 0.2s ease !important;
        }
        /* Hover affordance: swap the button's primary background to the
           theme's secondary color (with matching on-secondary text), so
           the hover reads as an intentional brand-color shift. Falls back
           to primary if no secondary is set. Suppressed while the form is
           submitting (:disabled). */
        .tb-form-wrap form button[type="submit"]:hover:not(:disabled) {
          background-color: var(
            --vibe-color-secondary,
            var(--vibe-color-primary)
          ) !important;
          color: var(--vibe-color-text-on-secondary, #ffffff) !important;
        }
        /* Two-step tour scheduler nav button ("Continue" on step 1,
           "Schedule Tour" on the final step — the same element, relabeled
           per step). The scheduler block renders this as a type="button"
           (its real submit is a hidden element), so the [type="submit"]
           rule above does not reach it. It is uniquely identified by
           .bg-primary + .ml-auto: the sibling "Back" button uses
           .bg-transparent, and selected time-slot buttons carry
           .bg-primary but never .ml-auto — so neither is affected. Apply
           the same secondary-color hover so it matches the submit button. */
        .tb-form-wrap button.bg-primary.ml-auto:hover:not(:disabled) {
          background-color: var(
            --vibe-color-secondary,
            var(--vibe-color-primary)
          ) !important;
          color: var(--vibe-color-text-on-secondary, #ffffff) !important;
        }
        .tb-form-wrap form button[type="submit"]::before {
          content: "SUBMIT";
          font-size: 0.9375rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .tb-form-wrap form button[type="submit"]:disabled::before {
          content: "SUBMITTING...";
        }
        .tb-form-empty {
          background: transparent;
          border: 1px dashed var(--vibe-color-border, #e2ddd5);
          padding: 2rem;
          text-align: center;
          color: var(--vibe-color-text-muted);
          font-family: var(--vibe-font-body);
        }

        /* Primary-color heading square — overlaps the top edge of the
           gray card on desktop so it extends upward into the hero image
           area above. Sharp corners, no border-radius. */
        .tb-callout {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          padding: 4.5rem 1.75rem;
          align-self: start;
          position: relative;
          z-index: 2;
        }
        @media (min-width: 768px) {
          .tb-callout { padding: 6rem 2.5rem; }
        }
        @media (min-width: 992px) {
          /* Pull the callout up so it overlaps:
             - the gray card top padding (~4rem = 64px)
             - the small white gap between hero and card (~2rem = 32px)
             - and a small portion of the hero image itself (~50px)
             ≈ -150px total */
          .tb-callout { margin-top: -150px; }
        }
        @media (min-width: 1200px) {
          .tb-callout { margin-top: -165px; }
        }
        .tb-callout-preheading {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.2;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin: 0 0 1.25rem;
          color: inherit;
          opacity: 0.85;
        }
        @media (min-width: 768px) {
          .tb-callout-preheading {
            font-size: 0.875rem;
            margin-bottom: 1.5rem;
          }
        }
        /* Heading sized down a tier from the previous 1.875rem/2.25rem
           pair so the callout reads with the same weight-and-presence
           hierarchy the rest of the home page now uses (lighter
           headings, heavier weight). Font-weight bumped 600 → 800 so
           the new smaller size still anchors the callout — same
           "smaller but bolder" treatment used elsewhere on the page. */
        .tb-callout-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.5rem;
          line-height: 1.2;
          font-weight: 800;
          letter-spacing: 0.01em;
          margin: 0 0 1.5rem;
          color: var(--vibe-color-secondary);
        }
        @media (min-width: 768px) {
          .tb-callout-heading {
            font-size: 1.75rem;
            margin-bottom: 1.75rem;
          }
        }
        /* Blurb sits one tier below the heading — comfortable body
           copy size (0.9375rem / 1rem) so the paragraph reads as
           supporting text rather than competing with the heading. */
        .tb-callout-blurb {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.6;
          color: inherit;
          opacity: 0.92;
        }
        @media (min-width: 768px) {
          .tb-callout-blurb {
            font-size: 1rem;
          }
        }
        .tb-callout-blurb p:last-child { margin-bottom: 0; }

        /* ============================================================
           RIGHT COLUMN — callout sits at the top (overlapping hero),
           lower-grid (logo | divider | info) stacks under it.
           ============================================================ */
        .tb-info-col {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .tb-info-col { gap: 2.5rem; }
        }

        /* Lower grid lives inside the right column. Max-width keeps the
           row compact; on mobile/tablet it stays centered, on desktop it
           shifts toward the left edge of the column (and slightly into
           the column gap) so the logo reads closer to the page center. */
        .tb-lower-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
          align-items: center;
          max-width: 560px;
          margin: 0 auto;
          width: 100%;
        }
        @media (min-width: 640px) {
          .tb-lower-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem;
            align-items: center;
            position: relative;
          }
          /* Vertical divider sits between the two columns */
          .tb-lower-grid::before {
            content: '';
            position: absolute;
            top: 0.5rem;
            bottom: 0.5rem;
            left: 50%;
            width: 1px;
            background: var(--vibe-color-border, #e2ddd5);
            transform: translateX(calc(-1.25rem - 0.5px));
          }
        }
        @media (min-width: 992px) {
          /* Pull the row toward the page center: left-align inside the
             right column and nudge it further left into the column gap. */
          .tb-lower-grid {
            margin-left: -2.5rem;
            margin-right: auto;
          }
        }

        /* Logo column — logo on top, social icon row below. */
        .tb-logo-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }
        @media (min-width: 768px) {
          .tb-logo-col { gap: 1.75rem; }
        }

        /* Logo — centered within its half of the lower grid.
           Logo fills the available column up to its natural aspect ratio,
           bounded by max-height so very tall logos do not crowd the gray
           card vertical edges. The wrapper adds a small inset so the
           logo never quite touches the column outer edges (the divider
           on one side, the card padding / column gap on the others). */
        .tb-logo-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 0.75rem 0.75rem;
        }
        @media (min-width: 768px) {
          .tb-logo-wrap { padding: 1rem 1rem; }
        }
        .tb-logo {
          width: 100%;
          max-width: 100%;
          height: auto;
          /* Mobile: stack layout, logo can grow tall (up to ~card padding). */
          max-height: 220px;
          object-fit: contain;
          display: block;
        }
        @media (min-width: 768px) {
          /* Tablet: 2-col lower grid kicks in at 640px; tablet card padding
             gives plenty of vertical room. */
          .tb-logo { max-height: 260px; }
        }
        @media (min-width: 992px) {
          /* Desktop: logo column is ~260px wide; allow a near-square logo
             box without crowding the divider above/below. */
          .tb-logo { max-height: 280px; }
        }
        .tb-logo-fallback {
          width: 100%;
          max-width: 220px;
          height: 80px;
          /* Matches the card background (#f3f4f6) so the empty-state
             placeholder stays invisible against the gray card. */
          background: #f3f4f6;
          border-radius: var(--vibe-radius-md, 0.25rem);
        }

        /* Social icon row — sits directly under the logo */
        .tb-social-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 0.15rem;
        }
        @media (min-width: 768px) {
          .tb-social-row { gap: 0.25rem; }
        }
        .tb-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.75rem;
          height: 1.75rem;
          border: 0;
          background: transparent;
          color: var(--vibe-color-primary);
          text-decoration: none;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .tb-social-link:hover,
        .tb-social-link:focus-visible {
          color: color-mix(in srgb, var(--vibe-color-primary) 75%, black);
          transform: translateY(-1px);
        }
        .tb-social-link:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .tb-social-icon {
          width: 1.1rem;
          height: 1.1rem;
        }

        /* Vertical contact-info stack (right side of lower grid) */
        .tb-info-list {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .tb-info-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.75rem;
        }
        /* Hours wraps to multiple rows — top-align the clock icon. */
        .tb-info-item-hours {
          align-items: flex-start;
        }
        .tb-info-item-hours .tb-info-icon {
          margin-top: 0.25rem;
        }
        .tb-info-icon {
          width: 1rem;
          height: 1rem;
          color: var(--vibe-color-primary);
          flex-shrink: 0;
        }
        .tb-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.5;
          /* Color comes from the .tb-info-list wrapper inline style
             (TEXT_ON_CARD = #1a1a1a). Children inherit so the whole
             stack stays in sync. */
          color: inherit;
          margin: 0;
        }
        .tb-info-link {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .tb-info-link:hover { color: var(--vibe-color-primary); }

        .tb-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }
        /* Day label and time sit close together on the left, not pushed
           to opposite ends of the row. */
        .tb-hours-row {
          display: flex;
          justify-content: flex-start;
          align-items: baseline;
          gap: 0.6rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
        }
        .tb-hours-day {
          color: inherit;
          font-weight: 500;
        }
        .tb-hours-time {
          /* Same color + weight as the address/phone/email and the day
             label — full-opacity inherited contrast color, default 400
             body weight (matches .tb-info-value). */
          color: inherit;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'TourBlock',
  displayName: 'Tour Block',
  tokens: {
    'tourblock.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'tourblock.form_heading': {
      label: 'Form Heading',
      type: 'richtext',
      defaultValue: 'Schedule your tour',
    },
    'tourblock.preheading': {
      label: 'Preheading',
      type: 'richtext',
      defaultValue: 'Plan Your Visit',
    },
    'tourblock.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: 'Schedule Your Tour',
    },
    'tourblock.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultValue:
        '<p>Pick a time that works for you and our leasing team will be ready to show you around in person.</p>',
    },
    'tourblock.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'tourblock.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
  },
  slotGroups: {
    'tourblock.hero_image': heroImageDef,
    'tourblock.logo': logoDef,
    'tourblock.email': emailLinkDef,
    'tourblock.facebook_link': facebookLinkDef,
    'tourblock.instagram_link': instagramLinkDef,
    'tourblock.google_link': googleLinkDef,
  },
  collectionBindings: {
    'tourblock.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(TourBlock as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourBlock;
